import { useState, FormEvent } from 'react';
import AppointmentPicker from './AppointmentPicker';

type FormState = 'idle' | 'submitting' | 'success' | 'error';

const spaceTypes = [
  '餐飲空間（咖啡廳、餐廳、飲品店）',
  '零售門市（品牌旗艦店、選物空間）',
  '辦公空間（企業辦公室、工作室）',
  '診所醫美空間',
  '接待會所 / VIP 空間',
  '商業展示空間 / 展廳',
  '複合式商業空間',
  '其他商業空間',
];

const budgets = [
  '100萬以下',
  '100萬 – 300萬',
  '300萬 – 600萬',
  '600萬 – 1,000萬',
  '1,000萬以上',
  '尚未確定，需要建議',
];

const timelines = [
  '1個月內希望開始',
  '1–3個月後',
  '3–6個月後',
  '6個月以上',
  '尚在規劃階段',
];

export default function ContactForm() {
  const [formState, setFormState] = useState<FormState>('idle');
  const [charCount, setCharCount] = useState(0);
  const [appointmentDate, setAppointmentDate] = useState('');
  const [appointmentTime, setAppointmentTime] = useState('');
  const MAX_CHARS = 500;

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const desc = (form.querySelector('[name="description"]') as HTMLTextAreaElement)?.value || '';
    if (desc.length > MAX_CHARS) return;

    setFormState('submitting');
    try {
      const data = new FormData(form);
      const body = new URLSearchParams();
      data.forEach((val, key) => body.append(key, val.toString()));
      if (appointmentDate) body.set('appointment_date', appointmentDate);
      if (appointmentTime) body.set('appointment_time', appointmentTime);

      const res = await fetch('https://readdy.ai/api/form/d7ftsu7imhg6tri2vlbg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      if (res.ok) {
        setFormState('success');
        form.reset();
        setCharCount(0);
        setAppointmentDate('');
        setAppointmentTime('');
      } else {
        setFormState('error');
      }
    } catch {
      setFormState('error');
    }
  };

  if (formState === 'success') {
    return (
      <div className="bg-[#fafaf8] rounded-2xl p-12 flex flex-col items-center text-center">
        <div className="w-16 h-16 flex items-center justify-center bg-[#b8956a]/15 rounded-full mb-6">
          <i className="ri-checkbox-circle-line text-[#b8956a] text-3xl"></i>
        </div>
        <h3 className="font-bold text-[#1a1a1a] mb-3" style={{ fontSize: '1.375rem' }}>
          諮詢申請已成功送出！
        </h3>
        <p className="text-[#5a5a5a] leading-relaxed mb-8 max-w-md" style={{ fontSize: '1.1875rem' }}>
          感謝你的信任，我們的顧問將在 <strong>24 小時內</strong>與你聯繫，
          確認諮詢時間與初步需求討論。
        </p>
        <button
          onClick={() => setFormState('idle')}
          className="inline-flex items-center gap-2 px-7 py-3 border border-[#e2ddd8] text-[#5a5a5a] rounded-full text-sm font-medium hover:bg-[#f4f2ef] transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-left-line"></i>
          返回填寫
        </button>
      </div>
    );
  }

  return (
    <form
      data-readdy-form
      id="contact-consultation-form"
      onSubmit={handleSubmit}
      className="bg-[#fafaf8] rounded-2xl p-7 md:p-10"
    >
      <h2 className="font-bold text-[#1a1a1a] mb-2" style={{ fontSize: '1.375rem' }}>
        預約商業空間規劃諮詢
      </h2>
      <p className="text-[#8a8a8a] text-sm mb-8">
        填寫以下資訊，讓我們更了解你的需求，為你安排最適合的顧問諮詢。
      </p>

      <div className="space-y-6">
        {/* Row 1: Name + Phone */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <label className="block text-sm font-medium text-[#1a1a1a] mb-2">
              姓名 <span className="text-[#b8956a]">*</span>
            </label>
            <input
              type="text"
              name="name"
              required
              placeholder="你的姓名"
              className="w-full px-4 py-3 bg-white border border-[#e2ddd8] rounded-xl text-sm text-[#1a1a1a] placeholder-[#c0bbb5] focus:outline-none focus:border-[#b8956a] transition-colors"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#1a1a1a] mb-2">
              聯絡電話 <span className="text-[#b8956a]">*</span>
            </label>
            <input
              type="tel"
              name="phone"
              required
              placeholder="09XX-XXX-XXX"
              className="w-full px-4 py-3 bg-white border border-[#e2ddd8] rounded-xl text-sm text-[#1a1a1a] placeholder-[#c0bbb5] focus:outline-none focus:border-[#b8956a] transition-colors"
            />
          </div>
        </div>

        {/* Row 2: Email + LINE */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <label className="block text-sm font-medium text-[#1a1a1a] mb-2">
              Email <span className="text-[#b8956a]">*</span>
            </label>
            <input
              type="email"
              name="email"
              required
              placeholder="yourname@email.com"
              className="w-full px-4 py-3 bg-white border border-[#e2ddd8] rounded-xl text-sm text-[#1a1a1a] placeholder-[#c0bbb5] focus:outline-none focus:border-[#b8956a] transition-colors"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#1a1a1a] mb-2">LINE ID</label>
            <input
              type="text"
              name="line_id"
              placeholder="你的 LINE ID"
              className="w-full px-4 py-3 bg-white border border-[#e2ddd8] rounded-xl text-sm text-[#1a1a1a] placeholder-[#c0bbb5] focus:outline-none focus:border-[#b8956a] transition-colors"
            />
          </div>
        </div>

        {/* Brand Name */}
        <div>
          <label className="block text-sm font-medium text-[#1a1a1a] mb-2">
            公司 / 品牌名稱
          </label>
          <input
            type="text"
            name="brand_name"
            placeholder="你的品牌或公司名稱"
            className="w-full px-4 py-3 bg-white border border-[#e2ddd8] rounded-xl text-sm text-[#1a1a1a] placeholder-[#c0bbb5] focus:outline-none focus:border-[#b8956a] transition-colors"
          />
        </div>

        {/* Space Type */}
        <div>
          <label className="block text-sm font-medium text-[#1a1a1a] mb-2">
            空間類型 <span className="text-[#b8956a]">*</span>
          </label>
          <select
            name="space_type"
            required
            defaultValue=""
            className="w-full px-4 py-3 bg-white border border-[#e2ddd8] rounded-xl text-sm text-[#1a1a1a] focus:outline-none focus:border-[#b8956a] transition-colors cursor-pointer appearance-none"
          >
            <option value="" disabled>請選擇空間類型</option>
            {spaceTypes.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>

        {/* Area + Budget */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <label className="block text-sm font-medium text-[#1a1a1a] mb-2">
              空間坪數（預估）
            </label>
            <input
              type="text"
              name="area"
              placeholder="例：60坪、約100坪"
              className="w-full px-4 py-3 bg-white border border-[#e2ddd8] rounded-xl text-sm text-[#1a1a1a] placeholder-[#c0bbb5] focus:outline-none focus:border-[#b8956a] transition-colors"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#1a1a1a] mb-2">
              裝修預算區間
            </label>
            <select
              name="budget"
              defaultValue=""
              className="w-full px-4 py-3 bg-white border border-[#e2ddd8] rounded-xl text-sm text-[#1a1a1a] focus:outline-none focus:border-[#b8956a] transition-colors cursor-pointer appearance-none"
            >
              <option value="" disabled>請選擇預算範圍</option>
              {budgets.map((b) => (
                <option key={b} value={b}>{b}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Timeline */}
        <div>
          <label className="block text-sm font-medium text-[#1a1a1a] mb-2">
            預計施工時程
          </label>
          <select
            name="timeline"
            defaultValue=""
            className="w-full px-4 py-3 bg-white border border-[#e2ddd8] rounded-xl text-sm text-[#1a1a1a] focus:outline-none focus:border-[#b8956a] transition-colors cursor-pointer appearance-none"
          >
            <option value="" disabled>請選擇預計時程</option>
            {timelines.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>

        {/* Appointment Picker */}
        <div>
          <label className="block text-sm font-medium text-[#1a1a1a] mb-1">
            預約諮詢時段
            <span className="ml-2 text-xs text-[#8a8a8a] font-normal">（可選填，讓我們更快安排）</span>
          </label>
          <p className="text-xs text-[#8a8a8a] mb-3">選擇你希望的諮詢日期與時間，我們將優先安排。</p>
          <AppointmentPicker
            selectedDate={appointmentDate}
            selectedTime={appointmentTime}
            onDateChange={setAppointmentDate}
            onTimeChange={setAppointmentTime}
          />
          {appointmentDate && appointmentTime && (
            <div className="mt-3 flex items-center gap-2 px-4 py-2.5 bg-[#b8956a]/10 border border-[#b8956a]/30 rounded-xl text-sm text-[#b8956a] font-medium">
              <i className="ri-calendar-check-line"></i>
              已選擇：{appointmentDate} {appointmentTime}
            </div>
          )}
        </div>

        {/* Has floor plan */}
        <div>
          <label className="block text-sm font-medium text-[#1a1a1a] mb-3">
            是否已有平面圖或現場照片？
          </label>
          <div className="flex flex-wrap gap-3">
            {['有平面圖', '有現場照片', '兩者都有', '目前都沒有'].map((opt) => (
              <label key={opt} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="has_files"
                  value={opt}
                  className="w-4 h-4 accent-[#b8956a] cursor-pointer"
                />
                <span className="text-sm text-[#5a5a5a]">{opt}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-[#1a1a1a] mb-2">
            需求說明
          </label>
          <textarea
            name="description"
            rows={5}
            placeholder="請簡要說明你的空間規劃需求、品牌背景或特別希望達成的目標……（最多 500 字）"
            maxLength={MAX_CHARS}
            onChange={(e) => setCharCount(e.target.value.length)}
            className="w-full px-4 py-3 bg-white border border-[#e2ddd8] rounded-xl text-sm text-[#1a1a1a] placeholder-[#c0bbb5] focus:outline-none focus:border-[#b8956a] transition-colors resize-none leading-relaxed"
          />
          <p className={`text-xs mt-1.5 text-right ${charCount >= MAX_CHARS ? 'text-red-400' : 'text-[#8a8a8a]'}`}>
            {charCount} / {MAX_CHARS}
          </p>
        </div>

        {/* Error */}
        {formState === 'error' && (
          <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
            <i className="ri-error-warning-line flex-shrink-0"></i>
            送出失敗，請稍後再試或直接來電 04-2202-3211。
          </div>
        )}

        {/* Submit */}
        <button
          type="submit"
          disabled={formState === 'submitting' || charCount > MAX_CHARS}
          className="w-full py-4 bg-[#b8956a] text-white font-semibold rounded-xl flex items-center justify-center gap-3 hover:bg-[#a07d58] disabled:opacity-60 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
          style={{ fontSize: '1.0625rem' }}
        >
          {formState === 'submitting' ? (
            <>
              <i className="ri-loader-4-line animate-spin"></i>
              送出中…
            </>
          ) : (
            <>
              <i className="ri-send-plane-line"></i>
              送出諮詢申請
            </>
          )}
        </button>

        <p className="text-xs text-center text-[#8a8a8a]">
          提交即表示你同意我們使用所填資訊與你聯繫，僅用於諮詢服務，不作其他用途。
        </p>
      </div>
    </form>
  );
}
